import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useReactToPrint } from 'react-to-print';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Button, Chip, CircularProgress,
  Alert, Grid, TextField, Tab, Tabs, IconButton, Tooltip, ButtonGroup, Checkbox
} from '@mui/material';
import { Restaurant, CheckCircle, PendingActions, NavigateBefore, NavigateNext, Today, Dashboard, ListAlt, Print } from '@mui/icons-material';
import { dietCoordinatorAPI } from '../services/api';

function DietCoordinatorDashboard() {
  const [viewMode, setViewMode] = useState(0); // 0 = Individual Meals, 1 = Consolidated Dashboard
  const [meals, setMeals] = useState([]);
  const [stats, setStats] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterMealType, setFilterMealType] = useState('all');
  const [deliveryNotes, setDeliveryNotes] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMeals, setSelectedMeals] = useState([]);
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    setSelectedMeals([]);
  }, [selectedDate, filterMealType, viewMode]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedMeals(selectableMeals.map((n) => n.id));
    } else {
      setSelectedMeals([]);
    }
  };

  const handleRowSelect = (id) => {
    setSelectedMeals(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleBulkDeliver = async () => {
    setBulkLoading(true);
    try {
      await dietCoordinatorAPI.bulkDeliver({
        mealIds: selectedMeals,
        deliveryNotes: bulkNotes
      });
      setSuccess('Selected meals marked as delivered');
      setSelectedMeals([]);
      setBulkNotes('');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to deliver selected meals');
    } finally {
      setBulkLoading(false);
    }
  };

  const dietDayPrintRef = useRef();
  const handlePrintDietDay = useReactToPrint({
    contentRef: dietDayPrintRef,
    documentTitle: `Diet Plan - ${selectedDate}`,
    pageStyle: '@page { size: A4 landscape; margin: 10mm; }'
  });

  useEffect(() => {
    loadData();
  }, [selectedDate, viewMode]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (viewMode === 0) {
        const [mealsRes, statsRes] = await Promise.all([
          dietCoordinatorAPI.getTodaysMeals(selectedDate),
          dietCoordinatorAPI.getTodaysStats(selectedDate)
        ]);
        setMeals(mealsRes.data.data || []);
        setStats(statsRes.data.data || []);
      } else {
        const dashboardRes = await dietCoordinatorAPI.getDashboard(selectedDate);
        setDashboardData(dashboardRes.data.data || dashboardRes.data);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDelivered = async (mealId) => {
    try {
      await dietCoordinatorAPI.markMealDelivered(mealId, { deliveryNotes: deliveryNotes[mealId] || '' });
      setSuccess('Meal marked as delivered');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to mark meal as delivered');
    }
  };

  const filteredMeals = filterMealType === 'all'
    ? meals
    : meals.filter(m => m.meal_time === filterMealType);

  const getMealTypeLabel = (type) => {
    const labels = { '7AM': 'Early Morning', 'Breakfast': 'Breakfast', '11AM': 'Mid Morning', 'Lunch': 'Lunch', '5PM': 'Evening', 'Dinner': 'Dinner' };
    return labels[type] || type;
  };

  const getDateLabel = () => {
    const today = new Date().toISOString().split('T')[0];
    const selected = new Date(selectedDate);
    const todayDate = new Date(today);

    if (selectedDate === today) return 'Today';
    if (selected < todayDate) return 'Past Date';
    return 'Future Date';
  };

  const isFutureDate = () => {
    const today = new Date().toISOString().split('T')[0];
    return selectedDate > today;
  };

  const selectableMeals = filteredMeals.filter(m => m.delivery_status === 'pending' && !isFutureDate());

  const handlePreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Restaurant sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h5" fontWeight="bold">Diet Coordinator Dashboard</Typography>
                <Typography variant="caption" color="text.secondary">
                  {getDateLabel()} - {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ButtonGroup variant="outlined" size="small">
                <Tooltip title="Previous Day">
                  <IconButton onClick={handlePreviousDay}>
                    <NavigateBefore />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Today">
                  <IconButton onClick={handleToday}>
                    <Today />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Next Day">
                  <IconButton onClick={handleNextDay}>
                    <NavigateNext />
                  </IconButton>
                </Tooltip>
              </ButtonGroup>
              <TextField
                type="date"
                label="Select Date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 180 }}
                size="small"
              />
              <Tooltip title="Print Day Diet Plan">
                <Button variant="outlined" size="small" startIcon={<Print />}
                  onClick={handlePrintDietDay} disabled={meals.length === 0}>
                  Print
                </Button>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={viewMode} onChange={(e, newValue) => setViewMode(newValue)}>
              <Tab icon={<ListAlt />} iconPosition="start" label="Individual Meals" />
              <Tab icon={<Dashboard />} iconPosition="start" label="Consolidated Dashboard" />
            </Tabs>
          </Box>

          {viewMode === 0 ? (
            <>
              {(selectedDate !== new Date().toISOString().split('T')[0]) && (
                <Alert severity={isFutureDate() ? 'info' : 'warning'} sx={{ mb: 2 }}>
                  {isFutureDate()
                    ? 'Viewing scheduled meals for a future date. Delivery actions are disabled.'
                    : 'Viewing historical meal data. All shown statuses are final.'}
                </Alert>
              )}

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {stats.length === 0 ? (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Typography color="text.secondary">No meals scheduled for this date</Typography>
                    </Paper>
                  </Grid>
                ) : (
                  stats.map((stat) => (
                    <Grid item xs={12} sm={6} md={2} key={stat.meal_time}>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" fontWeight="bold">{getMealTypeLabel(stat.meal_time)}</Typography>
                        <Typography variant="h4" color="primary.main">{stat.delivered}/{stat.total}</Typography>
                        <Typography variant="caption" color="text.secondary">Delivered / Total</Typography>
                      </Paper>
                    </Grid>
                  ))
                )}
              </Grid>

              <Tabs value={filterMealType} onChange={(e, val) => setFilterMealType(val)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tab label="All" value="all" />
                <Tab label="7AM" value="7AM" />
                <Tab label="Breakfast" value="Breakfast" />
                <Tab label="11AM" value="11AM" />
                <Tab label="Lunch" value="Lunch" />
                <Tab label="5PM" value="5PM" />
                <Tab label="Dinner" value="Dinner" />
              </Tabs>

              {selectedMeals.length > 0 && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50', border: 1, borderColor: 'primary.light', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {selectedMeals.length} meal(s) selected
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Bulk delivery notes"
                      value={bulkNotes}
                      onChange={(e) => setBulkNotes(e.target.value)}
                      sx={{ bgcolor: 'white', width: 250 }}
                    />
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={bulkLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                      onClick={handleBulkDeliver}
                      disabled={bulkLoading}
                    >
                      {bulkLoading ? 'Delivering...' : 'Deliver Selected'}
                    </Button>
                  </Box>
                </Paper>
              )}

              {filteredMeals.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <Typography color="text.secondary">No meals scheduled for this filter</Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        {!isFutureDate() && (
                          <TableCell style={{ width: '50px' }}>
                            <Checkbox
                              indeterminate={selectedMeals.length > 0 && selectedMeals.length < selectableMeals.length}
                              checked={selectableMeals.length > 0 && selectedMeals.length === selectableMeals.length}
                              onChange={handleSelectAllClick}
                            />
                          </TableCell>
                        )}
                        <TableCell><strong>Meal Type</strong></TableCell>
                        <TableCell><strong>Patient</strong></TableCell>
                        <TableCell><strong>Room</strong></TableCell>
                        <TableCell><strong>Diet Item</strong></TableCell>
                        <TableCell><strong>Instructions</strong></TableCell>
                        <TableCell><strong>Quantity</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredMeals.map((meal) => {
                        const isSelectable = meal.delivery_status === 'pending' && !isFutureDate();
                        const isSelected = selectedMeals.includes(meal.id);
                        return (
                          <TableRow key={meal.id} hover selected={isSelected}>
                            {!isFutureDate() && (
                              <TableCell>
                                {isSelectable ? (
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleRowSelect(meal.id)}
                                  />
                                ) : null}
                              </TableCell>
                            )}
                            <TableCell><Chip label={getMealTypeLabel(meal.meal_time)} size="small" color="primary" /></TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">{meal.first_name} {meal.last_name}</Typography>
                              <Typography variant="caption" color="text.secondary">{meal.patient_code}</Typography>
                            </TableCell>
                            <TableCell>{meal.room_number}</TableCell>
                            <TableCell>{meal.diet_item_name}</TableCell>
                            <TableCell style={{ color: '#d32f2f', fontWeight: 'medium' }}>
                              {meal.special_instructions || '—'}
                            </TableCell>
                            <TableCell>{meal.quantity}</TableCell>
                          <TableCell>
                            <Chip
                              label={meal.delivery_status === 'delivered' ? 'Delivered' : 'Pending'}
                              color={meal.delivery_status === 'delivered' ? 'success' : 'warning'}
                              size="small"
                              icon={meal.delivery_status === 'delivered' ? <CheckCircle /> : <PendingActions />}
                            />
                          </TableCell>
                          <TableCell>
                            {meal.delivery_status === 'pending' ? (
                              isFutureDate() ? (
                                <Typography variant="caption" color="text.secondary">
                                  Cannot deliver future meals
                                </Typography>
                              ) : (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <TextField
                                    size="small"
                                    placeholder="Notes (optional)"
                                    value={deliveryNotes[meal.id] || ''}
                                    onChange={(e) => setDeliveryNotes({ ...deliveryNotes, [meal.id]: e.target.value })}
                                    sx={{ width: 150 }}
                                  />
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    startIcon={<CheckCircle />}
                                    onClick={() => handleMarkDelivered(meal.id)}
                                  >
                                    Deliver
                                  </Button>
                                </Box>
                              )
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                By: {meal.delivered_by_name} <br />
                                At: {new Date(meal.delivered_at).toLocaleString()}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      )})}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : (
            <>
              {/* Consolidated Dashboard View */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                    <Typography variant="subtitle2">Total Patients</Typography>
                    <Typography variant="h3" fontWeight="bold">{dashboardData?.summary?.total_patients || 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Paper sx={{ p: 2, bgcolor: 'info.main', color: 'white' }}>
                    <Typography variant="subtitle2">Total Meals</Typography>
                    <Typography variant="h3" fontWeight="bold">{dashboardData?.summary?.total_meals || 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Paper sx={{ p: 2, bgcolor: 'success.main', color: 'white' }}>
                    <Typography variant="subtitle2">Delivered</Typography>
                    <Typography variant="h3" fontWeight="bold">{dashboardData?.summary?.delivered_meals || 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Paper sx={{ p: 2, bgcolor: 'warning.main', color: 'white' }}>
                    <Typography variant="subtitle2">Pending</Typography>
                    <Typography variant="h3" fontWeight="bold">{dashboardData?.summary?.pending_meals || 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Paper sx={{ p: 2, bgcolor: 'secondary.main', color: 'white' }}>
                    <Typography variant="subtitle2">Unique Items</Typography>
                    <Typography variant="h3" fontWeight="bold">{dashboardData?.summary?.unique_items || 0}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Meal Time Summary */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Meal Time Summary</Typography>
                  {dashboardData?.mealTimeSummary?.length === 0 ? (
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Typography color="text.secondary">No meal time data available</Typography>
                    </Paper>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell><strong>Meal Time</strong></TableCell>
                            <TableCell align="right"><strong>Patients</strong></TableCell>
                            <TableCell align="right"><strong>Total Meals</strong></TableCell>
                            <TableCell align="right"><strong>Delivered</strong></TableCell>
                            <TableCell align="right"><strong>Pending</strong></TableCell>
                            <TableCell align="right"><strong>Progress</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dashboardData?.mealTimeSummary?.map((row) => (
                            <TableRow key={row.meal_time} hover>
                              <TableCell>
                                <Chip label={getMealTypeLabel(row.meal_time)} color="primary" size="small" />
                              </TableCell>
                              <TableCell align="right">{row.patient_count}</TableCell>
                              <TableCell align="right">{row.meal_count}</TableCell>
                              <TableCell align="right">
                                <Chip label={row.delivered} color="success" size="small" />
                              </TableCell>
                              <TableCell align="right">
                                <Chip label={row.pending} color="warning" size="small" />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="bold" color="primary.main">
                                  {row.meal_count > 0 ? Math.round((row.delivered / row.meal_count) * 100) : 0}%
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>

              {/* Consolidated Items */}
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Consolidated Diet Items (Day-wise Total)</Typography>
                  {dashboardData?.consolidatedItems?.length === 0 ? (
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Typography color="text.secondary">No consolidated data available</Typography>
                    </Paper>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell><strong>Diet Item</strong></TableCell>
                            <TableCell><strong>Category</strong></TableCell>
                            <TableCell align="right"><strong>Patients</strong></TableCell>
                            <TableCell align="right"><strong>Total Count</strong></TableCell>
                            <TableCell align="right"><strong>Total Qty</strong></TableCell>
                            <TableCell align="right"><strong>Delivered Qty</strong></TableCell>
                            <TableCell align="right"><strong>Pending Qty</strong></TableCell>
                            <TableCell align="right"><strong>Delivered Count</strong></TableCell>
                            <TableCell align="right"><strong>Pending Count</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dashboardData?.consolidatedItems?.map((item, index) => (
                            <TableRow key={index} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">{item.diet_item_name}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={item.diet_item_category || 'N/A'} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell align="right">{item.patient_count}</TableCell>
                              <TableCell align="right">
                                <Chip label={item.total_count} size="small" color="primary" />
                              </TableCell>
                              <TableCell align="right">
                                <Chip label={item.total_quantity} size="small" color="info" />
                              </TableCell>
                              <TableCell align="right">
                                <Chip label={item.delivered_quantity} size="small" color="success" />
                              </TableCell>
                              <TableCell align="right">
                                <Chip label={item.pending_quantity} size="small" color="warning" />
                              </TableCell>
                              <TableCell align="right">{item.delivered_count}</TableCell>
                              <TableCell align="right">{item.pending_count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </CardContent>
      </Card>

      {/* Day diet plan print (off-screen portal) */}
      {createPortal(
        <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '270mm' }}>
          <div ref={dietDayPrintRef} style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#111', background: 'white', padding: '8mm 10mm' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #1b5e20', paddingBottom: 8, marginBottom: 10 }}>
              <img src="/logo.png" alt="MSG Logo" style={{ width: 48, height: 48, objectFit: 'contain', flexShrink: 0 }} />
              <div style={{ flex: 1, textAlign: 'center', padding: '0 8px' }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: '#1b5e20' }}>MSG NATUROPATHY CENTER</div>
                <div style={{ fontSize: 9, color: '#444' }}>Sirsa, Haryana &nbsp;|&nbsp; +91 7082999551 / 7082999351</div>
                <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4 }}>DAY DIET PLAN</div>
                <div style={{ fontSize: 10, color: '#555' }}>Date: {new Date(selectedDate).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</div>
              </div>
              <div style={{ fontSize: 9, color: '#888' }}>Printed: {new Date().toLocaleString('en-IN')}</div>
            </div>

            {/* Summary bar */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              {[
                { label: 'Total Meals', value: meals.length, color: '#1565c0' },
                { label: 'Delivered', value: meals.filter(m => m.delivery_status === 'delivered').length, color: '#2e7d32' },
                { label: 'Pending', value: meals.filter(m => m.delivery_status !== 'delivered').length, color: '#c62828' },
                { label: 'Patients', value: [...new Set(meals.map(m => m.patient_id))].length, color: '#6a1b9a' },
              ].map(c => (
                <div key={c.label} style={{ flex: 1, border: `1.5px solid ${c.color}`, borderRadius: 4, padding: '5px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: '#555' }}>{c.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: c.color }}>{c.value}</div>
                </div>
              ))}
            </div>

            {/* Grouped by meal time, then by patient */}
            {['7AM', 'Breakfast', '11AM', 'Lunch', '5PM', 'Dinner'].map(mealTime => {
              const timeItems = meals.filter(m => m.meal_time === mealTime);
              if (timeItems.length === 0) return null;
              const labels = { '7AM': 'Early Morning', 'Breakfast': 'Breakfast', '11AM': 'Mid Morning', 'Lunch': 'Lunch', '5PM': 'Evening', 'Dinner': 'Dinner' };
              // Group by patient
              const patientMap = {};
              timeItems.forEach(m => {
                const key = m.patient_code;
                if (!patientMap[key]) patientMap[key] = { ...m, items: [], instructions: [] };
                let itemStr = m.quantity ? `${m.diet_item_name} (${m.quantity})` : m.diet_item_name;
                if (m.special_instructions) {
                  itemStr += ` (Inst: ${m.special_instructions})`;
                }
                patientMap[key].items.push(itemStr);
                if (m.special_instructions) patientMap[key].instructions.push(m.special_instructions);
              });
              const patientRows = Object.values(patientMap);
              return (
                <div key={mealTime} style={{ marginBottom: 12 }}>
                  <div style={{ background: '#fff8e1', padding: '4px 8px', fontWeight: 700, fontSize: 11, marginBottom: 2, borderLeft: '4px solid #f57f17' }}>
                    {mealTime} — {labels[mealTime]} ({patientRows.length} patients, {timeItems.length} items)
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        {['#', 'Patient', 'Room', 'Diet Items', 'Instructions', 'Status'].map(h => (
                          <th key={h} style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: h === '#' || h === 'Status' ? 'center' : 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {patientRows.map((m, i) => {
                        const patientMeals = timeItems.filter(x => x.patient_code === m.patient_code);
                        const allDelivered = patientMeals.every(x => x.delivery_status === 'delivered');
                        return (
                          <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'center' }}>{i + 1}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600 }}>{m.first_name} {m.last_name}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{m.room_number}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{m.items.join(', ')}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 5px', color: '#555' }}>{m.instructions.join('; ') || '—'}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'center', color: allDelivered ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
                              {allDelivered ? 'DELIVERED' : 'PENDING'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </Box>
  );
}

export default DietCoordinatorDashboard;